import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react"
import authService from "./authService"
import { getPrimaryRole } from "./navigationConfig"
import {
    AuthContextValue,
    AuthResponse,
    AuthScope,
    AuthUser,
} from "../models/auth"
import { getErrorMessage } from "../models/api"

/**
 * Authentication Context
 * Provides auth state and methods throughout the app
 */

const defaultScope: AuthScope = {
    primaryRole: "cashier",
    roles: [],
    canAccessAdmin: false,
    canManageUsers: false,
    canUseWebhookApi: false,
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<AuthUser | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [posAutoCloseNotice, setPosAutoCloseNotice] = useState<string | null>(
        null
    )

    function pushPosAutoCloseNotice(message: string) {
        if (!message) return
        setPosAutoCloseNotice(message)
        try {
            localStorage.setItem("pos:auto-close-notice", message)
        } catch {}
    }

    function consumePosAutoCloseNotice(): string | null {
        const msg = posAutoCloseNotice
        setPosAutoCloseNotice(null)
        try {
            localStorage.removeItem("pos:auto-close-notice")
        } catch {}
        return msg
    }

    function buildScopeState(currentUser: AuthUser | null): AuthScope {
        const roles = Array.isArray(currentUser?.roles) ? currentUser!.roles : []
        const normalizedRoles = roles.map((role) => String(role))
        const primaryRole = getPrimaryRole(normalizedRoles)

        return {
            primaryRole,
            roles: normalizedRoles,
            canAccessAdmin: normalizedRoles.some((role) =>
                ["admin", "manager", "superadmin"].includes(role)
            ),
            canManageUsers: normalizedRoles.some((role) =>
                ["admin", "manager", "superadmin"].includes(role)
            ),
            canUseWebhookApi: normalizedRoles.includes("webhook"),
        }
    }

    useEffect(() => {
        loadUser()
    }, [])

    async function loadUser() {
        try {
            // SSO: partnerToken in URL always takes priority (replaces any existing session)
            const urlParams = new URLSearchParams(window.location.search)
            const urlToken = urlParams.get("partnerToken")

            if (urlToken) {
                try {
                    const response = await authService.partnerLogin(urlToken)
                    setUser(response.user)
                    // Clean up URL param after successful login
                    urlParams.delete("partnerToken")
                    const cleanUrl = urlParams.toString()
                        ? `${window.location.pathname}?${urlParams.toString()}`
                        : window.location.pathname
                    window.history.replaceState({}, "", cleanUrl)
                    return
                } catch (ssoErr) {
                    console.warn("SSO partner login failed:", ssoErr)
                }
            }

            if (authService.isAuthenticated()) {
                const currentUser = await authService.getCurrentUser()
                setUser(currentUser)
                try {
                    const persistedNotice = localStorage.getItem(
                        "pos:auto-close-notice"
                    )
                    if (persistedNotice) {
                        setPosAutoCloseNotice(persistedNotice)
                    }
                } catch {}
            } else {
                // SSO auto-login: fall back to shared localStorage token (same-origin prod)
                const sharedToken = localStorage.getItem("cileamoov_access_token")
                if (sharedToken) {
                    try {
                        const response = await authService.partnerLogin(
                            sharedToken
                        )
                        setUser(response.user)
                    } catch (ssoErr) {
                        localStorage.removeItem("cileamoov_access_token")
                        console.warn("SSO partner login failed:", ssoErr)
                    }
                }
            }
        } catch (err) {
            console.error("Failed to load user:", err)
            await authService.logout()
        } finally {
            setLoading(false)
        }
    }

    async function login(
        email: string,
        password: string
    ): Promise<AuthResponse> {
        try {
            setError(null)
            setLoading(true)
            const response = await authService.login(email, password)
            setUser(response.user)
            try {
                const persistedNotice = localStorage.getItem(
                    "pos:auto-close-notice"
                )
                if (persistedNotice) {
                    setPosAutoCloseNotice(persistedNotice)
                }
            } catch {}
            return response
        } catch (err: unknown) {
            setError(getErrorMessage(err, "Login failed"))
            throw err
        } finally {
            setLoading(false)
        }
    }

    async function register(
        userData: Record<string, unknown>
    ): Promise<AuthResponse> {
        try {
            setError(null)
            setLoading(true)
            const response = await authService.register(userData)
            setUser(response.user)
            return response
        } catch (err: unknown) {
            setError(getErrorMessage(err, "Registration failed"))
            throw err
        } finally {
            setLoading(false)
        }
    }

    async function logout() {
        try {
            setLoading(true)
            await authService.logout()
            setUser(null)
        } catch (err: unknown) {
            console.error("Logout error:", err)
        } finally {
            setLoading(false)
        }
    }

    async function refreshToken() {
        try {
            await authService.refreshAccessToken()
            const currentUser = await authService.getCurrentUser()
            setUser(currentUser)
        } catch (err: unknown) {
            console.error("Token refresh failed:", err)
            await logout()
            throw err
        }
    }

    const value = useMemo<AuthContextValue>(
        () => ({
            user,
            scope: user ? buildScopeState(user) : defaultScope,
            loading,
            error,
            posAutoCloseNotice,
            pushPosAutoCloseNotice,
            consumePosAutoCloseNotice,
            login,
            register,
            logout,
            refreshToken,
            isAuthenticated: !!user,
        }),
        [user, loading, error, posAutoCloseNotice]
    )

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider")
    }
    return context
}

export default AuthContext
